import {
  createTextCanvas,
  resolveTextStyle,
  textTextureKey,
  type CanvasFactory,
  type TextStyleOptions,
} from '../rendering/TextTexture';
import { type AssetManager } from './AssetManager';

/** A rasterized label: the key a sprite refers to it by, and how big it is in world units. */
export interface TextTextureAsset {
  /** Asset key the texture is stored under. */
  readonly key: string;
  /** Width in world units. */
  readonly width: number;
  /** Height in world units. */
  readonly height: number;
}

/**
 * Rasterizes text into a texture and registers it as an ordinary asset.
 *
 * Text is cached by content and style, which is exactly what an asset cache does, so it gets no cache
 * of its own: the key {@link textTextureKey} derives is the cache key, and drawing the same label
 * twice costs one map lookup. The label a sprite shows is then just another asset key.
 *
 * The returned size is what to give a {@link SpriteRenderer} so the text is drawn at its authored size
 * rather than stretched into whatever quad it landed on.
 */
export function ensureTextTexture(
  assets: AssetManager,
  text: string,
  options: TextStyleOptions = {},
  createCanvas?: CanvasFactory,
): TextTextureAsset {
  const key = textTextureKey(text, options);
  const texture = assets.getOrCreateTexture(key, () => createTextCanvas(text, options, createCanvas));
  const { resolution } = resolveTextStyle(options);

  return { key, width: texture.width / resolution, height: texture.height / resolution };
}
